import { DataNode } from "antd/lib/tree";
import { BeaconBase, Project, SiteWithBeacons } from "../../api/models";

export interface TreeNode extends DataNode {
  title: TreeTitle;
  id: string;
  key: string;
  icon?: React.ReactNode;
  displayVisibility?: boolean;
  children?: TreeNode[];
}

export interface TreeTitle {
  title: string;
  data: any;
}

export const filteredNodes = (
  node: TreeNode[],
  search: string | null
): [TreeNode[], string[]] => {
  if (!node || !Array.isArray(node)) return [[], []];

  let filteredNodesKey: string[] = [];

  node.forEach((node) => {
    if (
      !search ||
      node.title?.title.toString().toLowerCase().includes(search.toLowerCase())
    ) {
      if (node.children && Array.isArray(node.children)) {
        filteredNodesKey = [
          ...filteredNodesKey,
          node.key,
          ...filteredNodes(node.children, null)[1],
        ];
      } else {
        filteredNodesKey = [...filteredNodesKey, node.key];
      }
    } else {
      if (node.children && Array.isArray(node.children)) {
        let children = filteredNodes(node.children, search);
        if (children[0].length != 0) {
          filteredNodesKey = [...filteredNodesKey, node.key, ...children[1]];
        }
      }
    }
  });

  const filteredNodeData = filterTreeDataByKeys(node, filteredNodesKey);

  return [filteredNodeData, filteredNodesKey];
};

export const transformToTreeData = (
  projects: Project[] | undefined,
  isSolutionOperator: boolean
): TreeNode[] => {
  /*
    Data from API has org in a flat structure with projects. To display the data in a tree structure,
    we need to transform the data to a hierarchy where each org has its projects as children. 
  */
  const transformToHierarchy = (data: Project[] | undefined) => {
    const orgMap = new Map<string, any>();

    if (!data) return [];
    if (data.length === 0) return [];

    data?.forEach((project) => {
      const orgId = project.org.id;

      if (!orgMap.has(orgId)) {
        orgMap.set(orgId, {
          organizationId: orgId,
          organizationName: project.org.name,
          projects: [],
        });
      }

      orgMap.get(orgId)!.projects.push(project);
    });

    return Array.from(orgMap.values());
  };

  try {
    const projectsTransformed = transformToHierarchy(projects);

    const treeData = projectsTransformed.map((org) => ({
      id: "organizationName",
      key: org.organizationId,
      title: {
        title: org.organizationName,
        data: org,
      },
      displayVisibility: true,
      icon: <></>,
      children: org.projects.map((project: Project) => ({
        id: "constructionProjectName",
        key: project.id,
        title: {
          title: project.name,
          data: project,
        },
        displayVisibility: true,
        icon: <></>,
        children: project.sites.map((site: SiteWithBeacons) => ({
          id: "constructionSiteName",
          key: site.id,
          title: {
            title: site.name,
            data: {
              ...site,
              constructionProjectId: project.id,
              constructionProjectName: project.name,
              organizationId: org.organizationId,
            },
          },
          displayVisibility: true,
          icon: <></>,
          children: site.beacons.map((beacon: BeaconBase) => ({
            id: "serial",
            key: beacon.serial,
            title: {
              title: beacon.serial,
              data: beacon,
            },
            displayVisibility: true,
            icon: <></>,
          })),
        })),
      })),
    }));

    // If the user is a Solution Operator, return the top-level node (all data including organization)
    // Otherwise, return the children of the top-level node (all data excluding organization)
    return isSolutionOperator
      ? treeData
      : treeData.flatMap((node: TreeNode) => node.children || []);
  } catch (error) {
    return [];
  }
};

export const filterTreeDataByKeys = (
  data: TreeNode[],
  keys: string[]
): TreeNode[] => {
  const keySet = new Set(keys);

  const filterNodes = (nodes: TreeNode[]): TreeNode[] => {
    return nodes
      .map((node) => {
        if (keySet.has(node.key)) {
          const filteredChildren = node.children
            ? filterNodes(node.children)
            : [];
          return {
            ...node,
            children: filteredChildren.length ? filteredChildren : undefined,
          };
        }
        return null;
      })
      .filter((node) => node !== null) as TreeNode[];
  };

  return filterNodes(data);
};

export const getAllKeys = (treeData: TreeNode[] | undefined): string[] => {
  if (!treeData) {
    return [];
  }
  const keys: string[] = [];

  const traverse = (nodes: TreeNode[]) => {
    nodes.forEach((node) => {
      if (node.key) {
        keys.push(node.key);
      }
      if (node.children) {
        traverse(node.children);
      }
    });
  };

  traverse(treeData);

  return keys;
};

export const getChildKeys = (node: TreeNode | undefined): string[] => {
  if (!node) return [];

  const keys: string[] = [node.key];

  const traverse = (nodes: TreeNode[]) => {
    nodes.forEach((childNode) => {
      if (childNode.key) {
        keys.push(childNode.key);
      }
      if (childNode.children) {
        traverse(childNode.children);
      }
    });
  };

  if (node.children) {
    traverse(node.children);
  }

  return keys;
};

export const getParentKeys = (key: string, tree: TreeNode[]): string[] => {
  const parentKeys: string[] = [];

  const traverse = (nodes: TreeNode[], parent: string | undefined) => {
    nodes.forEach((node) => {
      if (node.key === key) {
        if (parent) {
          parentKeys.push(parent);
          parentKeys.push(...getParentKeys(parent, tree));
        }
      }
      if (node.children) {
        traverse(node.children, node.key);
      }
    });
  };

  traverse(tree, undefined);

  return parentKeys;
};

export const findNode = (
  nodes: TreeNode[],
  key: string
): TreeNode | undefined => {
  for (const node of nodes) {
    if (node.key === key) {
      return node;
    }
    if (node.children) {
      const foundNode = findNode(node.children, key);
      if (foundNode) {
        return foundNode;
      }
    }
  }
  return undefined;
};
